/**
 * Redirects — Client-side redirect handler for legacy URLs
 * Maps old WordPress/blog/testimonial URLs to their new destinations
 */
import { useEffect } from "react";
import { useLocation } from "wouter";

const EXACT_REDIRECTS: Record<string, string> = {
  // Old content-plan URL → admin
  "/content-plan": "/admin/content-plan",
  // Old blog posts → /blog
  "/blog/seo-role-in-business-value": "/blog",
  "/blog/geo-seo-vs-ai-seo-which-model-builds-more-defensible-growth": "/blog",
  "/blog/will-backlink-lose-value": "/blog",
  "/blog/do-we-need-to-rethink-seo-budgets-for-ai": "/blog",
  "/blog/boards-evaluating-seo-performance": "/blog",
  "/blog/how-does-seo-compound-over-time-like-an-asset-not-just-a-tactic": "/blog",
  "/blog/ai-search-change-brand-visibility": "/blog",
  "/blog/connect-seo-metrics-to-revenue": "/blog",
  "/blog/what-happens-to-digital-asset-after-acquisition": "/blog",
  "/blog/pr-vs-backlink-buying": "/blog",
  "/blog/paid-vs-editorial-backlinks": "/blog",
  "/blog/right-time-invest-in-seo": "/blog",
  "/blog/traditional-seo-becomes-risk": "/blog",
  "/blog/companies-treat-seo-as-cost-center": "/blog",
  "/blog/complete-guide-to-backlinks": "/blog",
  "/blog/roi-of-seo-vs-other-growth-channels": "/blog",
  "/blog/ai-overviews-make-clicks-disappear": "/blog",
  "/blog/growth-strategies-generative-engine-optimization": "/blog",
  "/blog/generative-engine-optimization": "/blog",
  "/blog/track-brand-visibility-in-ai-results": "/blog",
  "/blog/business-case-for-seo": "/blog",
  "/blog/outcome-of-delayed-seo-investment": "/blog",
  "/blog/ai-and-the-next-era-of-search-what-business-leaders-need-to-know": "/blog",
  "/blog/how-long-seo-takes-to-show-results": "/blog",
  "/blog/backlink-budget-per-month": "/blog",
  "/blog/seo-budget-allocation-guide": "/blog",
  "/blog/seo-for-recruiters": "/blog",
  "/blog/ppc-data-inform-seo-strategy": "/blog",
  "/blog/seo-outperform-paid-acquisition": "/blog",
  "/blog/run-seo-and-brand-strategy-together": "/blog",
  "/blog/the-modern-seo-stack-tools-and-systems-for-100m-companies": "/blog",
  "/blog/create-seo-content-strategy": "/blog",

  // Old testimonials/case study → /client-stories
  "/testimonials": "/client-stories",
  "/testimonials/rachel": "/client-stories",
  "/case-study/tofu": "/client-stories",
  "/client-stories/ecommerce-retailer": "/client-stories",

  // Old services pages → /services
  "/services-old-2": "/services",
  "/services-old": "/services",

  // Old SEO agency location page → SEO agency service page
  "/seo-agency/ontario": "/seo-agency",

  // Old homepage variant → homepage
  "/home2": "/",

  // AI Overview Checker retired — superseded by LLM Visibility Checker
  "/tools/ai-overview-checker": "/tools/llm-visibility-checker",

  // Old blog/article, case study, work, stories pages
  "/blog-article": "/blog",
  "/case-study": "/client-stories",
  "/work/increasing-organic-traffic-by-1272-for-a-legal-firm-in-manchester": "/client-stories",
  "/seo-agency-old": "/seo-agency",
  "/stories": "/client-stories",
  "/work/boosting-executive-recruitment-firms-organic-traffic-by-50-in-2-months": "/client-stories",

  // Old location pages → SEO agency service page
  "/seo-agency/cerritos": "/seo-agency",

  // Feed & tag pages → blog
  "/feed": "/blog",
  "/tag/news": "/blog",
};

/** Prefix-based redirects for wildcard patterns */
const PREFIX_REDIRECTS: { prefix: string; target: string }[] = [
  // Author pagination → author page
  { prefix: "/author/anjanluthra/page/", target: "/author/anjanluthra" },

  // WordPress internals → homepage
  { prefix: "/wp-content/", target: "/" },
  { prefix: "/wp-", target: "/" },
];

/** Query string patterns that should redirect to homepage */
const QUERY_REDIRECTS = [
  "ae_global_templates",
  "page_id",
];

export default function Redirects() {
  const [location, setLocation] = useLocation();

  useEffect(() => {
    const path = location;
    const search = window.location.search;
    const params = new URLSearchParams(search);

    // 1. Check query string redirects
    for (const key of QUERY_REDIRECTS) {
      if (params.has(key)) {
        setLocation("/");
        return;
      }
    }

    // 2. Check exact path redirects
    const exactTarget = EXACT_REDIRECTS[path] || EXACT_REDIRECTS[path.replace(/\/$/, "")];
    if (exactTarget) {
      setLocation(exactTarget);
      return;
    }

    // 3. Check prefix-based redirects
    for (const { prefix, target } of PREFIX_REDIRECTS) {
      if (path.startsWith(prefix)) {
        setLocation(target);
        return;
      }
    }
  }, [location, setLocation]);

  return null;
}
